import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import type { Locale, LocalizedText, StoreType } from "@globaldine/contracts";
import { localize, supportedLocales } from "@globaldine/contracts";
import { bestLocale } from "@globaldine/i18n";
import {
  Badge,
  Button,
  Logo,
  Panel,
  StatCard,
  formatMoney,
} from "@globaldine/ui";
import {
  Activity,
  BadgeDollarSign,
  Bell,
  Building2,
  Check,
  ChevronRight,
  CircleDollarSign,
  Database,
  FileCheck2,
  Flag,
  Globe2,
  Languages,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  Megaphone,
  MoreHorizontal,
  Network,
  Plus,
  RefreshCw,
  ShieldCheck,
  Store,
  Users,
  WalletCards,
  Webhook,
  Wifi,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import {
  adminTranslator,
  auditActionKey,
  translateAdminError,
  type AdminMessageKey,
} from "./messages";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000";
const TOKEN_KEY = "globaldine.admin.token";
const LOCALE_KEY = "globaldine.admin.locale";

const intlLocales: Record<Locale, string> = {
  en: "en-US",
  vi: "vi-VN",
  "zh-Hans": "zh-CN",
  "zh-Hant": "zh-TW",
  ko: "ko-KR",
  ru: "ru-RU",
  ja: "ja-JP",
  th: "th-TH",
};

function initialLocale(): Locale {
  const stored = localStorage.getItem(LOCALE_KEY);
  return supportedLocales.includes(stored as Locale)
    ? (stored as Locale)
    : bestLocale(navigator.language);
}

let sessionToken = localStorage.getItem(TOKEN_KEY) ?? "";

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    throw new Error("NETWORK_ERROR");
  }
  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
  } & T;
  if (!response.ok) throw new Error(body.error ?? "INTERNAL_ERROR");
  return body;
}

type Page =
  | "overview"
  | "merchants"
  | "countries"
  | "localization"
  | "payments"
  | "ads"
  | "trust";
type Translator = ReturnType<typeof adminTranslator>;

type PlatformData = {
  metrics: {
    merchants: number;
    liveStores: number;
    ordersToday: number;
    grossMerchantSales: number;
    platformHeldFunds: number;
    languages: number;
    countriesReady: number;
  };
  countries: Array<{
    code: string;
    name: string;
    stores: number;
    localePack: number;
    payments: string;
  }>;
  stores: Array<{
    id: string;
    name: string | LocalizedText;
    country: string;
    type: StoreType;
    currency: string;
    status: string;
  }>;
  adapters: Array<{ kind: string; name: string; status: string }>;
  recentAudit: Array<{
    id: string;
    action: string;
    actor: string;
    entityId: string;
    at: string;
  }>;
};

type ViewProps = {
  data: PlatformData;
  locale: Locale;
  t: Translator;
  setPage: (page: Page) => void;
  onAddMerchant?: () => void;
};

const navDefinitions: Array<{
  id: Page;
  label: AdminMessageKey;
  icon: LucideIcon;
}> = [
  { id: "overview", label: "navOverview", icon: LayoutDashboard },
  { id: "merchants", label: "navMerchants", icon: Building2 },
  { id: "countries", label: "navCountries", icon: Globe2 },
  { id: "localization", label: "navLocalization", icon: Languages },
  { id: "payments", label: "navPayments", icon: WalletCards },
  { id: "ads", label: "navAds", icon: Megaphone },
  { id: "trust", label: "navTrust", icon: ShieldCheck },
];

function LanguageSelect({
  locale,
  t,
  onChange,
  compact = false,
}: {
  locale: Locale;
  t: Translator;
  onChange: (locale: Locale) => void;
  compact?: boolean;
}) {
  return (
    <select
      className={`select${compact ? " compact" : ""}`}
      aria-label={t("language")}
      title={t("language")}
      value={locale}
      onChange={(event) => onChange(event.target.value as Locale)}
    >
      {supportedLocales.map((code) => (
        <option key={code} value={code}>
          {languageName(code)}
        </option>
      ))}
    </select>
  );
}

function countryName(code: string, locale: Locale) {
  try {
    return (
      new Intl.DisplayNames([intlLocales[locale]], { type: "region" }).of(
        code,
      ) ?? code
    );
  } catch {
    return code;
  }
}

function languageName(code: string) {
  try {
    return (
      new Intl.DisplayNames([intlLocales[code as Locale]], {
        type: "language",
      }).of(code) ?? code
    );
  } catch {
    return code;
  }
}

function translatedStoreName(name: string | LocalizedText, locale: Locale) {
  return typeof name === "string" ? name : localize(name, locale);
}

function statusLabel(status: string, t: Translator) {
  const statusKeys: Record<string, AdminMessageKey> = {
    live: "statusLive",
    ready: "statusReady",
    reserved: "statusReserved",
    demo: "statusDemo",
    "offline-confirmation": "statusOffline",
    preparing: "statusPreparing",
    draft: "statusDraft",
  };
  return t(statusKeys[status] ?? "pending");
}

function paymentModeLabel(value: string, t: Translator) {
  if (value.includes("merchant-direct"))
    return `${t("merchantDirect")} · ${t("statusOffline")}`;
  if (value.includes("reserved")) return t("statusReserved");
  return t("pending");
}

function storeTypeLabel(type: StoreType, t: Translator) {
  const keys: Record<StoreType, AdminMessageKey> = {
    full_service: "fullService",
    drink: "drinkStore",
    quick_service: "quickService",
    cafe: "cafe",
    food_court: "foodCourt",
  };
  return t(keys[type]);
}

function adapterName(name: string, t: Translator) {
  const keys: Record<string, AdminMessageKey> = {
    "VN merchant QR / POS": "adapterVnMerchant",
    "Global provider contract": "adapterGlobalContract",
    "Web Push": "adapterWebPush",
    "LAN / CUPS gateway": "adapterPrinter",
  };
  return t(keys[name] ?? "adapterGlobalContract");
}

function Stat({
  label,
  value,
  detail,
  icon,
}: {
  label: string;
  value: string | number;
  detail: string;
  icon: ReactNode;
}) {
  return <StatCard label={label} value={value} detail={detail} icon={icon} />;
}

function OverviewPage({ data, locale, t, setPage }: ViewProps) {
  return (
    <>
      <div className="content-head">
        <div>
          <h1>{t("overviewTitle")}</h1>
          <p>{t("overviewSubtitle")}</p>
        </div>
        <Badge tone="green">
          <Wifi size={11} /> {t("systemHealthy")}
        </Badge>
      </div>
      <div className="stats-grid">
        <Stat
          label={t("liveStores")}
          value={data.metrics.liveStores}
          detail={t("merchantsDemo", { count: data.metrics.merchants })}
          icon={<Store />}
        />
        <Stat
          label={t("ordersToday")}
          value={data.metrics.ordersToday}
          detail={t("allChannels")}
          icon={<Activity />}
        />
        <Stat
          label={t("merchantSales")}
          value={formatMoney(
            data.metrics.grossMerchantSales,
            "VND",
            intlLocales[locale],
          )}
          detail={t("merchantDirectSettlement")}
          icon={<CircleDollarSign />}
        />
        <Stat
          label={t("platformHeldFunds")}
          value={formatMoney(
            data.metrics.platformHeldFunds,
            "VND",
            intlLocales[locale],
          )}
          detail={t("productBoundary")}
          icon={<LockKeyhole />}
        />
      </div>
      <div className="dashboard-grid">
        <Panel
          title={t("countryProgress")}
          action={
            <Button
              variant="ghost"
              className="small"
              onClick={() => setPage("countries")}
            >
              {t("viewCountryPacks")} <ChevronRight size={14} />
            </Button>
          }
        >
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("market")}</th>
                <th>{t("stores")}</th>
                <th>{t("localePack")}</th>
                <th>{t("paymentMode")}</th>
                <th>{t("status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.countries.map((country) => (
                <tr key={country.code}>
                  <td>
                    <strong>{country.code}</strong> ·{" "}
                    {countryName(country.code, locale)}
                  </td>
                  <td>{country.stores}</td>
                  <td>
                    <div style={{ width: 120 }}>
                      <div className="progress">
                        <span style={{ width: `${country.localePack}%` }} />
                      </div>
                      <span className="tiny muted">{country.localePack}%</span>
                    </div>
                  </td>
                  <td>{paymentModeLabel(country.payments, t)}</td>
                  <td>
                    <Badge
                      tone={country.localePack === 100 ? "green" : "amber"}
                    >
                      {country.localePack === 100
                        ? t("statusLive")
                        : t("statusPreparing")}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <div className="stack">
          <Panel title={t("systemBoundary")}>
            <div className="panel-body stack">
              <div className="split">
                <span>{t("merchantOwnPayment")}</span>
                <Badge tone="green">
                  <Check size={11} /> {t("enforced")}
                </Badge>
              </div>
              <div className="split">
                <span>{t("platformWallet")}</span>
                <Badge>{t("notImplemented")}</Badge>
              </div>
              <div className="split">
                <span>{t("cardStorage")}</span>
                <Badge>{t("notImplemented")}</Badge>
              </div>
              <div className="split">
                <span>{t("tenantIsolation")}</span>
                <Badge tone="green">{t("required")}</Badge>
              </div>
            </div>
          </Panel>
          <Panel title={t("adapterHealth")}>
            <div className="panel-body stack">
              {data.adapters.map((adapter) => (
                <div className="split" key={adapter.name}>
                  <span>
                    <Network size={13} style={{ verticalAlign: -2 }} />{" "}
                    {adapterName(adapter.name, t)}
                  </span>
                  <Badge
                    tone={
                      adapter.status === "ready"
                        ? "green"
                        : adapter.status === "reserved"
                          ? "purple"
                          : "blue"
                    }
                  >
                    {statusLabel(adapter.status, t)}
                  </Badge>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}

function MerchantsPage({ data, locale, t, onAddMerchant }: ViewProps) {
  return (
    <>
      <div className="content-head">
        <div>
          <h1>{t("merchantsTitle")}</h1>
          <p>{t("merchantsSubtitle")}</p>
        </div>
        <Button onClick={onAddMerchant}>
          <Plus size={16} /> {t("addMerchant")}
        </Button>
      </div>
      <div className="stats-grid">
        <Stat
          label={t("merchantEntities")}
          value={data.metrics.merchants}
          detail={t("kycReserved")}
          icon={<Building2 />}
        />
        <Stat
          label={t("onlineStores")}
          value={data.metrics.liveStores}
          detail={t("allOnline")}
          icon={<Store />}
        />
        <Stat
          label={t("chainBrands")}
          value="0"
          detail={t("multiStoreReady")}
          icon={<Network />}
        />
        <Stat
          label={t("saasSubscription")}
          value={t("testPlan")}
          detail={t("billingReserved")}
          icon={<BadgeDollarSign />}
        />
      </div>
      <Panel>
        <table className="data-table">
          <thead>
            <tr>
              <th>{t("store")}</th>
              <th>{t("country")}</th>
              <th>{t("template")}</th>
              <th>{t("settlement")}</th>
              <th>{t("status")}</th>
              <th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {data.stores.map((store) => (
              <tr key={store.id}>
                <td>
                  <strong>{translatedStoreName(store.name, locale)}</strong>
                  <span className="tiny muted" style={{ display: "block" }}>
                    {store.id}
                  </span>
                </td>
                <td>{countryName(store.country, locale)}</td>
                <td>{storeTypeLabel(store.type, t)}</td>
                <td>
                  {store.currency} · {t("merchantDirect")}
                </td>
                <td>
                  <Badge tone="green">{statusLabel(store.status, t)}</Badge>
                </td>
                <td>
                  <button
                    className="icon-button"
                    aria-label={t("actions")}
                    title={t("actions")}
                  >
                    <MoreHorizontal size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </>
  );
}

function CountriesPage({ data, locale, t }: ViewProps) {
  const foundations: Array<{ icon: ReactNode; title: string; detail: string }> =
    [
      {
        icon: <Globe2 />,
        title: "BCP 47 / CLDR",
        detail: t("standardsDetail"),
      },
      {
        icon: <Database />,
        title: t("regionalData"),
        detail: t("regionalDataDetail"),
      },
      {
        icon: <Workflow />,
        title: t("countryAdapters"),
        detail: t("countryAdaptersDetail"),
      },
      {
        icon: <FileCheck2 />,
        title: t("legalVersions"),
        detail: t("legalVersionsDetail"),
      },
    ];
  return (
    <>
      <div className="content-head">
        <div>
          <h1>{t("countriesTitle")}</h1>
          <p>{t("countriesSubtitle")}</p>
        </div>
        <Button>
          <Plus size={16} /> {t("createCountryPack")}
        </Button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
          gap: 15,
        }}
      >
        {data.countries.map((country) => (
          <article className="panel" key={country.code}>
            <div className="panel-body">
              <div className="split">
                <div className="logo-mark">
                  <Flag />
                </div>
                <Badge tone={country.localePack === 100 ? "green" : "amber"}>
                  {country.localePack === 100
                    ? t("statusLive")
                    : t("statusDraft")}
                </Badge>
              </div>
              <h2 style={{ margin: "18px 0 4px" }}>
                {countryName(country.code, locale)}{" "}
                <span className="muted">{country.code}</span>
              </h2>
              <p className="muted tiny">{t("countryCapabilities")}</p>
              <div className="progress" style={{ margin: "16px 0 5px" }}>
                <span style={{ width: `${country.localePack}%` }} />
              </div>
              <div className="split tiny">
                <span>
                  {t("percentComplete", { percent: country.localePack })}
                </span>
                <span>{t("storeCount", { count: country.stores })}</span>
              </div>
              <Button
                variant="secondary"
                style={{ width: "100%", marginTop: 18 }}
              >
                {t("openCountryPack")} <ChevronRight size={14} />
              </Button>
            </div>
          </article>
        ))}
      </div>
      <Panel title={t("globalFoundation")} className="panel">
        <div
          className="panel-body"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            gap: 14,
          }}
        >
          {foundations.map((item) => (
            <div key={item.title}>
              {item.icon}
              <h4 style={{ margin: "8px 0 3px" }}>{item.title}</h4>
              <span className="tiny muted">{item.detail}</span>
            </div>
          ))}
        </div>
      </Panel>
    </>
  );
}

function LocalizationPage({ data, locale, t }: ViewProps) {
  const quality: Array<{
    code: Locale;
    customer: string;
    menu: string;
    reviewer: AdminMessageKey;
  }> = [
    { code: "vi", customer: "100%", menu: "100%", reviewer: "localTeam" },
    { code: "zh-Hans", customer: "100%", menu: "100%", reviewer: "merchant" },
    { code: "zh-Hant", customer: "98%", menu: "96%", reviewer: "pending" },
    { code: "en", customer: "100%", menu: "100%", reviewer: "localTeam" },
    { code: "ko", customer: "94%", menu: "87%", reviewer: "pending" },
    { code: "ru", customer: "96%", menu: "91%", reviewer: "pending" },
    { code: "ja", customer: "96%", menu: "92%", reviewer: "pending" },
    { code: "th", customer: "91%", menu: "84%", reviewer: "pending" },
  ];
  return (
    <>
      <div className="content-head">
        <div>
          <h1>{t("localizationTitle")}</h1>
          <p>{t("localizationSubtitle")}</p>
        </div>
        <Button>
          <Plus size={16} /> {t("addLanguagePack")}
        </Button>
      </div>
      <div className="stats-grid">
        <Stat
          label={t("launchLanguages")}
          value={data.metrics.languages}
          detail={t("extensibleTags")}
          icon={<Languages />}
        />
        <Stat
          label={t("menuTranslationCoverage")}
          value="93%"
          detail={t("merchantReviewRequired")}
          icon={<FileCheck2 />}
        />
        <Stat
          label={t("rateSnapshot")}
          value={t("fifteenMinutes")}
          detail={t("providerFallback")}
          icon={<RefreshCw />}
        />
        <Stat
          label={t("rtlReadiness")}
          value={t("statusReserved")}
          detail={t("layoutPrepared")}
          icon={<Globe2 />}
        />
      </div>
      <Panel title={t("languageQuality")}>
        <table className="data-table">
          <thead>
            <tr>
              <th>{t("language")}</th>
              <th>{t("customerUi")}</th>
              <th>{t("menuContent")}</th>
              <th>{t("ticketRendering")}</th>
              <th>{t("reviewer")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {quality.map((row) => (
              <tr key={row.code}>
                <td>
                  <strong>{languageName(row.code)}</strong>
                  <span className="tiny muted" style={{ display: "block" }}>
                    {row.code}
                  </span>
                </td>
                <td>{row.customer}</td>
                <td>{row.menu}</td>
                <td>✓</td>
                <td>{t(row.reviewer)}</td>
                <td>
                  <Badge tone={row.customer === "100%" ? "green" : "amber"}>
                    {row.customer === "100%"
                      ? t("statusReady")
                      : t("statusReview")}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Panel title={t("amountRules")}>
        <div className="panel-body">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
              gap: 15,
            }}
          >
            <div>
              <strong>{t("officialAmount")}</strong>
              <p className="tiny muted">{t("officialAmountDetail")}</p>
            </div>
            <div>
              <strong>{t("approximateAmount")}</strong>
              <p className="tiny muted">{t("approximateAmountDetail")}</p>
            </div>
            <div>
              <strong>{t("rateNotice")}</strong>
              <p className="tiny muted">{t("rateNoticeDetail")}</p>
            </div>
          </div>
        </div>
      </Panel>
    </>
  );
}

function PaymentsPage({ t }: ViewProps) {
  return (
    <>
      <div className="content-head">
        <div>
          <h1>{t("paymentsTitle")}</h1>
          <p>{t("paymentsSubtitle")}</p>
        </div>
        <Button>
          <Plus size={16} /> {t("registerAdapter")}
        </Button>
      </div>
      <div className="stats-grid">
        <Stat
          label={t("platformFunds")}
          value="0"
          detail={t("noWallet")}
          icon={<LockKeyhole />}
        />
        <Stat
          label={t("productionTrigger")}
          value={t("cashier")}
          detail={t("statusOffline")}
          icon={<Users />}
        />
        <Stat
          label={t("paymentWebhook")}
          value={t("statusReserved")}
          detail={t("signedIdempotent")}
          icon={<Webhook />}
        />
        <Stat
          label={t("reconciliation")}
          value={t("contractReady")}
          detail={t("providerRecords")}
          icon={<CircleDollarSign />}
        />
      </div>
      <Panel title={t("paymentMatrix")}>
        <table className="data-table">
          <thead>
            <tr>
              <th>{t("marketProvider")}</th>
              <th>{t("accountOwner")}</th>
              <th>{t("create")}</th>
              <th>{t("webhook")}</th>
              <th>{t("refund")}</th>
              <th>{t("settlement")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>{t("offlineMethod")}</strong>
              </td>
              <td>{t("merchant")}</td>
              <td>{t("cashierAction")}</td>
              <td>—</td>
              <td>{t("manual")}</td>
              <td>{t("merchant")}</td>
              <td>
                <Badge tone="green">{t("statusLive")}</Badge>
              </td>
            </tr>
            <tr>
              <td>{t("vietnamAdapter")}</td>
              <td>{t("merchant")}</td>
              <td>{t("contract")}</td>
              <td>{t("contract")}</td>
              <td>{t("contract")}</td>
              <td>{t("direct")}</td>
              <td>
                <Badge tone="purple">{t("statusReserved")}</Badge>
              </td>
            </tr>
            <tr>
              <td>{t("globalAdapter")}</td>
              <td>{t("merchant")}</td>
              <td>{t("contract")}</td>
              <td>{t("contract")}</td>
              <td>{t("contract")}</td>
              <td>{t("direct")}</td>
              <td>
                <Badge tone="purple">{t("statusReserved")}</Badge>
              </td>
            </tr>
          </tbody>
        </table>
      </Panel>
      <Panel title={t("unifiedContract")}>
        <div
          className="panel-body"
          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          {[
            "listMethods",
            "createPayment",
            "queryPayment",
            "closePayment",
            "verifyWebhook",
            "refund",
            "queryRefund",
            "reconcile",
          ].map((method) => (
            <Badge tone="blue" key={method}>
              {method}()
            </Badge>
          ))}
          <p
            className="muted tiny"
            style={{ width: "100%", margin: "13px 0 0" }}
          >
            {t("noSilentSwitch")}
          </p>
        </div>
      </Panel>
    </>
  );
}

function AdsPage({ t }: ViewProps) {
  const rows: Array<AdminMessageKey[]> = [
    [
      "surfaceNearby",
      "platform",
      "nativeCard",
      "geoLanguageTime",
      "storeViewOrder",
      "sponsoredLabel",
    ],
    [
      "surfaceQueue",
      "platformConsent",
      "nativeCard",
      "storeWaitTime",
      "reservationRedeem",
      "merchantConsent",
    ],
    [
      "surfaceCompleted",
      "platform",
      "offerCard",
      "cuisineLocale",
      "couponRedeem",
      "noDisruption",
    ],
    [
      "surfaceMenu",
      "merchant",
      "selfPromotion",
      "ownTraffic",
      "itemAddOrder",
      "noCompetitor",
    ],
    [
      "surfaceReceipt",
      "merchant",
      "receiptFooter",
      "storeRules",
      "couponRedeem",
      "optIn",
    ],
  ];
  return (
    <>
      <div className="content-head">
        <div>
          <h1>{t("adsTitle")}</h1>
          <p>{t("adsSubtitle")}</p>
        </div>
        <Badge tone="purple">{t("featureOff")}</Badge>
      </div>
      <div className="stats-grid">
        <Stat
          label={t("platformSlots")}
          value="7"
          detail={t("discoveryQueuePostOrder")}
          icon={<Megaphone />}
        />
        <Stat
          label={t("merchantSlots")}
          value="2"
          detail={t("ownMenuReceipt")}
          icon={<Store />}
        />
        <Stat
          label={t("attributionEvents")}
          value="5"
          detail={t("attributionFlow")}
          icon={<Workflow />}
        />
        <Stat
          label={t("competitorAds")}
          value="0"
          detail={t("explicitlyProhibited")}
          icon={<ShieldCheck />}
        />
      </div>
      <Panel title={t("reservedPlacements")}>
        <table className="data-table">
          <thead>
            <tr>
              <th>{t("store")}</th>
              <th>{t("owner")}</th>
              <th>{t("format")}</th>
              <th>{t("targeting")}</th>
              <th>{t("attribution")}</th>
              <th>{t("rule")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row[0]}>
                {row.map((key, index) => (
                  <td key={key}>
                    {index === 0 ? <strong>{t(key)}</strong> : t(key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Panel title={t("billingGovernance")}>
        <div
          className="panel-body"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
            gap: 18,
          }}
        >
          <div>
            <strong>CPD / CPM / CPC / CPA</strong>
            <p className="tiny muted">{t("campaignBilling")}</p>
          </div>
          <div>
            <strong>{t("advertiserQualification")}</strong>
            <p className="tiny muted">{t("countryVerification")}</p>
          </div>
          <div>
            <strong>{t("fraudPrevention")}</strong>
            <p className="tiny muted">{t("anomalyDetection")}</p>
          </div>
          <div>
            <strong>{t("trafficOwnership")}</strong>
            <p className="tiny muted">{t("consentShare")}</p>
          </div>
        </div>
      </Panel>
    </>
  );
}

function TrustPage({ data, locale, t }: ViewProps) {
  const controls: AdminMessageKey[] = [
    "controlTenant",
    "controlRbac",
    "controlPresence",
    "controlWebhook",
    "controlIdempotency",
    "controlCredentials",
    "controlBackup",
  ];
  return (
    <>
      <div className="content-head">
        <div>
          <h1>{t("trustTitle")}</h1>
          <p>{t("trustSubtitle")}</p>
        </div>
        <Badge tone="green">
          <ShieldCheck size={12} /> {t("baselineActive")}
        </Badge>
      </div>
      <div className="stats-grid">
        <Stat
          label={t("maliciousOrderBlock")}
          value={t("geofence")}
          detail={t("presenceFallback")}
          icon={<ShieldCheck />}
        />
        <Stat
          label={t("printDuplicateRate")}
          value="0"
          detail={t("idempotencyKeys")}
          icon={<Workflow />}
        />
        <Stat
          label={t("collectionRisk")}
          value={t("none")}
          detail={t("fundsNeverEnter")}
          icon={<LockKeyhole />}
        />
        <Stat
          label={t("auditEvents")}
          value={data.recentAudit.length}
          detail={t("rollingLog")}
          icon={<Database />}
        />
      </div>
      <div className="dashboard-grid">
        <Panel title={t("recentAudit")}>
          {data.recentAudit.length ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t("event")}</th>
                  <th>{t("actor")}</th>
                  <th>{t("entity")}</th>
                  <th>{t("time")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recentAudit.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <Badge tone="blue">
                        {t(auditActionKey(entry.action))}
                      </Badge>
                    </td>
                    <td>{entry.actor}</td>
                    <td>{entry.entityId.slice(0, 12)}</td>
                    <td>
                      {new Date(entry.at).toLocaleTimeString(
                        intlLocales[locale],
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        },
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <Database />
              <strong>{t("noAudit")}</strong>
            </div>
          )}
        </Panel>
        <div className="stack">
          <Panel title={t("coreSecurity")}>
            <div className="panel-body stack">
              {controls.map((key) => (
                <div key={key} className="split">
                  <span className="tiny">{t(key)}</span>
                  <Badge tone="green">
                    <Check size={10} /> {t("implemented")}
                  </Badge>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title={t("locationMinimization")}>
            <div className="panel-body">
              <p className="muted tiny">{t("locationMinimizationDetail")}</p>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}

export function App() {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [token, setToken] = useState(sessionToken);
  const [authorized, setAuthorized] = useState(false);
  const [credentials, setCredentials] = useState({
    merchantCode: "",
    username: "",
    password: "",
  });
  const [page, setPage] = useState<Page>("overview");
  const [data, setData] = useState<PlatformData>();
  const [toast, setToast] = useState("");
  const [merchantOpen, setMerchantOpen] = useState(false);
  const [merchantBusy, setMerchantBusy] = useState(false);
  const [merchantForm, setMerchantForm] = useState({
    merchantCode: "",
    storeName: "",
    username: "owner",
    password: "",
    preferredLanguage: "zh-Hans" as Locale,
    country: "VN",
    currency: "VND",
    storeType: "full_service" as StoreType,
  });
  const t = adminTranslator(locale);
  const nav = navDefinitions.map((item) => ({
    ...item,
    labelText: t(item.label),
  }));

  function changeLocale(nextLocale: Locale) {
    setLocale(nextLocale);
    setToast("");
    localStorage.setItem(LOCALE_KEY, nextLocale);
  }

  function logout() {
    sessionToken = "";
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthorized(false);
    setData(undefined);
  }

  async function refresh() {
    try {
      setData(await api<PlatformData>("/api/dashboard/platform"));
    } catch (error) {
      setToast(translateAdminError((error as Error).message, locale));
    }
  }

  useEffect(() => {
    document.documentElement.lang = intlLocales[locale];
    document.title = `${t("navOverview")} · GlobeDine`;
  }, [locale]);

  useEffect(() => {
    if (!token) return;
    api<{ user: { roles: string[]; preferredLanguage?: Locale } }>(
      "/api/auth/me",
    )
      .then((result) => {
        if (!result.user.roles.includes("owner"))
          throw new Error("PLATFORM_OWNER_REQUIRED");
        if (!localStorage.getItem(LOCALE_KEY) && result.user.preferredLanguage)
          changeLocale(result.user.preferredLanguage);
        setAuthorized(true);
        void refresh();
      })
      .catch((error) => {
        logout();
        setToast(translateAdminError((error as Error).message, locale));
      });
  }, [token]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  async function login(event: FormEvent) {
    event.preventDefault();
    try {
      const result = await api<{
        token: string;
        user: { roles: string[]; preferredLanguage?: Locale };
      }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(credentials),
      });
      if (!result.user.roles.includes("owner"))
        throw new Error("PLATFORM_OWNER_REQUIRED");
      sessionToken = result.token;
      localStorage.setItem(TOKEN_KEY, result.token);
      if (!localStorage.getItem(LOCALE_KEY) && result.user.preferredLanguage)
        changeLocale(result.user.preferredLanguage);
      setToken(result.token);
      setAuthorized(true);
    } catch (error) {
      setToast(translateAdminError((error as Error).message, locale));
    }
  }

  async function createMerchant(event: FormEvent) {
    event.preventDefault();
    setMerchantBusy(true);
    try {
      await api("/api/platform/merchants", {
        method: "POST",
        body: JSON.stringify(merchantForm),
      });
      setMerchantOpen(false);
      setMerchantForm({
        merchantCode: "",
        storeName: "",
        username: "owner",
        password: "",
        preferredLanguage: locale,
        country: "VN",
        currency: "VND",
        storeType: "full_service",
      });
      await refresh();
      setToast(t("statusReady"));
    } catch (error) {
      setToast(translateAdminError((error as Error).message, locale));
    } finally {
      setMerchantBusy(false);
    }
  }

  if (!token || !authorized)
    return (
      <div className="auth-page">
        <form className="auth-card stack" onSubmit={login}>
          <div className="split">
            <Logo />
            <LanguageSelect
              locale={locale}
              t={t}
              onChange={changeLocale}
              compact
            />
          </div>
          <div>
            <h1>{t("adminLogin")}</h1>
            <p className="muted">{t("ownerOnly")}</p>
          </div>
          <label className="field">
            <span>{t("merchantCode")}</span>
            <input
              className="input"
              value={credentials.merchantCode}
              onChange={(event) =>
                setCredentials({
                  ...credentials,
                  merchantCode: event.target.value,
                })
              }
              required
            />
          </label>
          <label className="field">
            <span>{t("username")}</span>
            <input
              className="input"
              value={credentials.username}
              onChange={(event) =>
                setCredentials({ ...credentials, username: event.target.value })
              }
              required
            />
          </label>
          <label className="field">
            <span>{t("password")}</span>
            <input
              className="input"
              type="password"
              value={credentials.password}
              onChange={(event) =>
                setCredentials({ ...credentials, password: event.target.value })
              }
              required
            />
          </label>
          <Button>{t("signIn")}</Button>
          {toast && <div className="form-error">{toast}</div>}
        </form>
      </div>
    );

  const viewProps: ViewProps = {
    data: data!,
    locale,
    t,
    setPage,
    onAddMerchant: () => setMerchantOpen(true),
  };
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Logo />
        <nav className="nav-list">
          {nav.map(({ id, labelText, icon: Icon }) => (
            <button
              className={`nav-item ${page === id ? "active" : ""}`}
              key={id}
              onClick={() => setPage(id)}
            >
              <Icon size={18} />
              <span>{labelText}</span>
              {id === "ads" && <Badge tone="purple">{t("later")}</Badge>}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="split">
            <span>
              <ShieldCheck
                size={13}
                style={{ color: "var(--brand)", verticalAlign: -2 }}
              />{" "}
              {t("controlPlane")}
            </span>
          </div>
          <p style={{ margin: "10px 0 0" }}>{t("region")}: APAC-1 · v1.0.12</p>
        </div>
      </aside>
      <div className="main-area">
        <header className="topbar">
          <div className="topbar-title">
            <h1>{nav.find((item) => item.id === page)?.labelText}</h1>
            <p>{t("systemSubtitle")}</p>
          </div>
          <div className="topbar-actions">
            <LanguageSelect
              locale={locale}
              t={t}
              onChange={changeLocale}
              compact
            />
            <div className="field">
              <input className="input" placeholder={t("searchPlaceholder")} />
            </div>
            <button
              className="icon-button"
              aria-label={t("refresh")}
              title={t("refresh")}
              onClick={() => void refresh()}
            >
              <RefreshCw size={17} />
            </button>
            <button
              className="icon-button"
              aria-label={t("notifications")}
              title={t("notifications")}
            >
              <Bell size={17} />
              <span className="dot" />
            </button>
            <button
              className="icon-button"
              aria-label={t("signOut")}
              title={t("signOut")}
              onClick={logout}
            >
              <LogOut size={17} />
            </button>
          </div>
        </header>
        <main className="content">
          {!data ? (
            <div className="empty-state">
              <div className="logo-mark">
                <Globe2 />
              </div>
              <strong>{t("loading")}</strong>
            </div>
          ) : (
            <>
              {page === "overview" && <OverviewPage {...viewProps} />}
              {page === "merchants" && <MerchantsPage {...viewProps} />}
              {page === "countries" && <CountriesPage {...viewProps} />}
              {page === "localization" && <LocalizationPage {...viewProps} />}
              {page === "payments" && <PaymentsPage {...viewProps} />}
              {page === "ads" && <AdsPage {...viewProps} />}
              {page === "trust" && <TrustPage {...viewProps} />}
            </>
          )}
        </main>
      </div>
      {merchantOpen && (
        <div className="modal-backdrop">
          <form className="modal" onSubmit={createMerchant}>
            <div className="modal-header">
              <h2>{t("addMerchant")}</h2>
              <button
                className="icon-button"
                type="button"
                onClick={() => setMerchantOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content stack">
              <label className="field">
                <span>{t("merchantCode")}</span>
                <input
                  className="input"
                  pattern="[A-Za-z0-9_-]+"
                  value={merchantForm.merchantCode}
                  onChange={(e) =>
                    setMerchantForm({
                      ...merchantForm,
                      merchantCode: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <label className="field">
                <span>{t("store")}</span>
                <input
                  className="input"
                  value={merchantForm.storeName}
                  onChange={(e) =>
                    setMerchantForm({
                      ...merchantForm,
                      storeName: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <div className="settings-grid">
                <label className="field">
                  <span>{t("country")}</span>
                  <input
                    className="input"
                    maxLength={2}
                    value={merchantForm.country}
                    onChange={(e) =>
                      setMerchantForm({
                        ...merchantForm,
                        country: e.target.value.toUpperCase(),
                      })
                    }
                    required
                  />
                </label>
                <label className="field">
                  <span>{t("settlement")}</span>
                  <input
                    className="input"
                    maxLength={3}
                    value={merchantForm.currency}
                    onChange={(e) =>
                      setMerchantForm({
                        ...merchantForm,
                        currency: e.target.value.toUpperCase(),
                      })
                    }
                    required
                  />
                </label>
              </div>
              <label className="field">
                <span>{t("template")}</span>
                <select
                  className="select"
                  value={merchantForm.storeType}
                  onChange={(e) =>
                    setMerchantForm({
                      ...merchantForm,
                      storeType: e.target.value as StoreType,
                    })
                  }
                >
                  <option value="full_service">{t("fullService")}</option>
                  <option value="drink">{t("drinkStore")}</option>
                  <option value="quick_service">{t("quickService")}</option>
                  <option value="cafe">{t("cafe")}</option>
                  <option value="food_court">{t("foodCourt")}</option>
                </select>
              </label>
              <label className="field">
                <span>{t("language")}</span>
                <select
                  className="select"
                  value={merchantForm.preferredLanguage}
                  onChange={(e) =>
                    setMerchantForm({
                      ...merchantForm,
                      preferredLanguage: e.target.value as Locale,
                    })
                  }
                >
                  {supportedLocales.map((code) => (
                    <option key={code} value={code}>
                      {languageName(code)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t("username")}</span>
                <input
                  className="input"
                  value={merchantForm.username}
                  onChange={(e) =>
                    setMerchantForm({
                      ...merchantForm,
                      username: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <label className="field">
                <span>{t("password")}</span>
                <input
                  className="input"
                  type="password"
                  minLength={10}
                  value={merchantForm.password}
                  onChange={(e) =>
                    setMerchantForm({
                      ...merchantForm,
                      password: e.target.value,
                    })
                  }
                  required
                />
              </label>
            </div>
            <div className="modal-footer">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setMerchantOpen(false)}
              >
                ×
              </Button>
              <Button disabled={merchantBusy}>{t("create")}</Button>
            </div>
          </form>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
